import type { MantineColorsTuple } from "@mantine/core";

/**
 * Semantic accent ramps and the neutral scales (docs/UI_STANDARDS.md). With `surfaces.ts`, this is the only place in
 * the repository where product colors are written as hex.
 *
 * Every ramp is built around one anchor, which is the vivid dark-mode tone (index 5). Indexes 0-4 are tints toward
 * white for light surfaces, 6-9 are progressively darker for readable text and controls on white. `primaryShade`
 * is `{ light: 7, dark: 5 }`, so light mode takes a darkened shade and dark mode the vivid anchor.
 *
 * `deepen` raises the darkening applied to 6-9 for hues that are intrinsically light (green, amber, gold): without it
 * they never reach 4.5:1 against a white page. The exact values were tuned against the contrast tests in
 * `theme.test.ts`, which assert every ramp in both schemes rather than trusting these numbers.
 */
interface Anchors {
  /** The vivid tone, placed at index 5 and used by dark mode. */
  readonly anchor: string;
  /** Multiplier on the darkening of shades 6-9; >1 for light hues that need more to pass contrast on white. */
  readonly deepen?: number;
}

const ANCHORS = {
  /**
   * The accent, not the primary fill: focus rings, form-field focus, links, selection, informational state.
   * This is the sign-in page's blue, which the owner chose as the brand color on 2026-09-17.
   */
  brand: { anchor: "#3B82F6", deepen: 1.15 },
  /** Positive, completed, favorable, healthy, connected, live. */
  teal: { anchor: "#2ED68A", deepen: 1.85 },
  /** Negative, failed, loss, destructive, risk. Coral rather than harsh pure red. */
  red: { anchor: "#FF6575", deepen: 1.5 },
  /** Operational caution. */
  orange: { anchor: "#F6B84A", deepen: 1.95 },
  /** Rank, reward, champion, premium distinction. Never a second brand color. */
  yellow: { anchor: "#F2C14E", deepen: 2 },
  /** Informational. */
  cyan: { anchor: "#4DB8FF", deepen: 1.5 },
  /** Secondary special state. */
  violet: { anchor: "#9B7BFF", deepen: 1.25 },
  /** Kept for chart series that need a sixth distinguishable hue. */
  indigo: { anchor: "#6E8BFF", deepen: 1.25 },
} as const satisfies Readonly<Record<string, Anchors>>;

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const toHex = (v: number): string =>
  Math.round(Math.min(255, Math.max(0, v)))
    .toString(16)
    .padStart(2, "0");
function mix(hex: string, target: readonly [number, number, number], amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `#${toHex(r + (target[0] - r) * amount)}${toHex(g + (target[1] - g) * amount)}${toHex(b + (target[2] - b) * amount)}`;
}
const WHITE = [255, 255, 255] as const;
const BLACK = [0, 0, 0] as const;

export function buildRamp({ anchor: rawAnchor, deepen = 1 }: Anchors): MantineColorsTuple {
  /** Normalized so every shade in a ramp is written the same way. */
  const anchor = rawAnchor.toLowerCase();
  const darken = (amount: number): string => mix(anchor, BLACK, Math.min(0.9, amount * deepen));
  return [
    mix(anchor, WHITE, 0.92),
    mix(anchor, WHITE, 0.8),
    mix(anchor, WHITE, 0.62),
    mix(anchor, WHITE, 0.4),
    mix(anchor, WHITE, 0.2),
    anchor,
    darken(0.1),
    darken(0.26),
    darken(0.42),
    darken(0.56),
  ];
}

/**
 * Near-black dashboard ground with charcoal layers. Mantine's mapping decides the index meanings: 0-1 text, 2 dimmed
 * text, 3 placeholder, 4 default border, 5 hover, 6 card/input surface, 7 body. Index 8 is the sunken well, which sits
 * one step *above* the page ground by design, and 9 is the deepest recess.
 */
export const DARK_SCALE: MantineColorsTuple = [
  "#f5f7fa",
  "#b5bdc8",
  "#7f8996",
  "#59616d",
  "#252a31",
  "#171a1f",
  "#111317",
  "#090a0c",
  "#0c0e11",
  "#060709",
];

/**
 * Light neutrals. Mantine's mapping: 0-1 surfaces and tracks, 2-4 borders, 5-7 secondary and muted text, 8-9 primary
 * text. The page ground itself stays pure white.
 */
export const GRAY_SCALE: MantineColorsTuple = [
  "#f9fafb",
  "#f6f7f8",
  "#e5e7eb",
  "#d1d5db",
  "#b8c0ca",
  "#a7adb5",
  "#7c848e",
  "#5f6670",
  "#3a4049",
  "#14171a",
];

export const PALETTE_NAMES = Object.keys(ANCHORS) as readonly (keyof typeof ANCHORS)[];
/** Every accent ramp name the theme registers. */
export type PaletteName = (typeof PALETTE_NAMES)[number];
export const RAMPS: Readonly<Record<string, MantineColorsTuple>> = Object.fromEntries(
  Object.entries(ANCHORS).map(([name, anchors]) => [name, buildRamp(anchors)]),
);
